import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from './lib/cn';

export interface EmptyStateProps {
  /** lucide-react icon shown in a tinted circle. */
  icon?: LucideIcon;
  /** Or a bespoke illustration (e.g. the sidebar balloons). Wins over `icon`. */
  illustration?: ReactNode;
  title: string;
  description?: string;
  /** Primary/secondary action buttons. */
  action?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** `error` tints the glyph red, `success` green. */
  tone?: 'neutral' | 'brand' | 'error' | 'success';
  className?: string;
}

const SIZE = {
  sm: { wrap: 'py-6 gap-2', glyph: 'h-10 w-10', icon: 20, title: 'text-md', body: 'text-sm' },
  md: { wrap: 'py-10 gap-3', glyph: 'h-14 w-14', icon: 24, title: 'text-lg', body: 'text-md' },
  lg: { wrap: 'py-16 gap-4', glyph: 'h-20 w-20', icon: 32, title: 'text-2xl', body: 'text-md' },
} as const;

const TONE = {
  neutral: 'bg-secondary text-muted-foreground',
  brand: 'bg-primary-subtle text-primary',
  error: 'bg-destructive-subtle text-destructive',
  success: 'bg-success-subtle text-success',
} as const;

/** Empty / zero-data / not-found state. Every list must render one of these. */
export function EmptyState({
  icon: Icon, illustration, title, description, action,
  size = 'md', tone = 'brand', className,
}: EmptyStateProps) {
  const s = SIZE[size];

  return (
    <div
      role="status"
      className={cn('flex flex-col items-center justify-center text-center', s.wrap, className)}
    >
      {illustration ?? (
        Icon ? (
          <span className={cn('inline-flex items-center justify-center rounded-pill', s.glyph, TONE[tone])}>
            <Icon size={s.icon} strokeWidth={1.75} aria-hidden />
          </span>
        ) : null
      )}

      <h3 className={cn('font-semibold text-foreground', s.title)}>{title}</h3>

      {description ? (
        <p className={cn('max-w-sm text-muted-foreground', s.body)}>{description}</p>
      ) : null}

      {action ? <div className="mt-1 flex items-center gap-2">{action}</div> : null}
    </div>
  );
}
